//
// # Rotation
//
// Enumerated rotations in increments of 90 degrees
//

// Local Imports
import { exhaust } from "../errors";

// Enumerated Rotations
// in increments of 90 degrees
export type Rotation = 0 | 90 | 180 | 270;
export const Rotation = {
  R0: 0,
  R90: 90,
  R180: 180,
  R270: 270,
  next,
  prev,
  sum,
  diff,
  relative,
  radiansClockwise,
  radiansCounterClockwise,
  inverse,
} as const;

export function next(rotation: Rotation): Rotation {
  switch (rotation) {
    case Rotation.R0:
      return Rotation.R90;
    case Rotation.R90:
      return Rotation.R180;
    case Rotation.R180:
      return Rotation.R270;
    case Rotation.R270:
      return Rotation.R0;
    default:
      exhaust(rotation); // Exhaustiveness check
  }
}

// Get the previous `Rotation` in the sequence
export function prev(rotation: Rotation): Rotation {
  switch (rotation) {
    case Rotation.R0:
      return Rotation.R270;
    case Rotation.R90:
      return Rotation.R0;
    case Rotation.R180:
      return Rotation.R90;
    case Rotation.R270:
      return Rotation.R180;
    default:
      exhaust(rotation); // Exhaustiveness check
  }
}

// Take the sum of two Rotations
export function sum(a: Rotation, b: Rotation): Rotation {
  switch (a) {
    case Rotation.R0:
      return b;
    case Rotation.R90:
      return next(b);
    case Rotation.R180:
      return next(next(b));
    case Rotation.R270:
      return next(next(next(b)));
    default:
      exhaust(a);
  }
}

// Take the difference between two Rotations
export function diff(lhs: Rotation, rhs: Rotation): Rotation {
  switch (lhs) {
    case Rotation.R0:
      return rhs;
    case Rotation.R90:
      return prev(rhs);
    case Rotation.R180:
      return prev(prev(rhs));
    case Rotation.R270:
      return prev(prev(prev(rhs)));
    default:
      exhaust(lhs);
  }
}

export function relative(rot: Rotation, relativeTo: Rotation): Rotation {
  return diff(rot, relativeTo);
}

// Get a number of radians clockwise
export function radiansClockwise(rotation: Rotation): number {
  switch (rotation) {
    case Rotation.R0:
      return 0;
    case Rotation.R90:
      return -Math.PI / 2;
    case Rotation.R180:
      return Math.PI;
    case Rotation.R270:
      return Math.PI / 2;
    default:
      exhaust(rotation);
  }
}

// Get a number of radians counterclockwise
export function radiansCounterClockwise(rotation: Rotation): number {
  switch (rotation) {
    case Rotation.R0:
      return 0;
    case Rotation.R90:
      return Math.PI / 2;
    case Rotation.R180:
      return Math.PI;
    case Rotation.R270:
      return -Math.PI / 2;
    default:
      exhaust(rotation);
  }
}

// Get the inverse rotation
export function inverse(rotation: Rotation): Rotation {
  switch (rotation) {
    case Rotation.R0:
      return Rotation.R0;
    case Rotation.R90:
      return Rotation.R270;
    case Rotation.R180:
      return Rotation.R180;
    case Rotation.R270:
      return Rotation.R90;
    default:
      exhaust(rotation);
  }
}
